import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";

import { FinancialDecision, IVehicle, VehicleStatus } from "../../models/Vehicle";
import openInNewTab from "../../utils/openInNewTab";
import classNames from "../../utils/classNames";
import Tooltip from "../shared/Tooltip";

type SourcingVehicle = Pick<IVehicle, "id" | "biddingComplete" | "status" | "financialDecision">;

// Sourcing/bidding can happen right up to Awaiting Dismantling; once dismantling starts (or the
// vehicle is rejected) the status is no longer actionable, so the badge should not be shown.
export function sourcingStatusApplies(vehicle: SourcingVehicle): boolean {
  return (
    vehicle.status <= VehicleStatus["Awaiting Dismantling"] &&
    vehicle.financialDecision !== FinancialDecision.Rejected
  );
}

export default function VehicleSourcingStatusBadge({
  vehicle,
  linkToPricing = false,
}: {
  vehicle: SourcingVehicle;
  linkToPricing?: boolean;
}) {
  if (!sourcingStatusApplies(vehicle)) return null;

  const complete = vehicle.biddingComplete;

  const badge = (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-nowrap",
        complete ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700",
        linkToPricing ? "cursor-pointer hover:ring-1 hover:ring-current" : ""
      )}
    >
      {complete ? (
        <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
      ) : (
        <ExclamationCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
      )}
      {complete ? "Sourcing complete" : "Sourcing outstanding"}
    </span>
  );

  const tooltip = complete
    ? "Bidding / sourcing has been marked complete"
    : "Bidding / sourcing has not been marked complete";

  if (!linkToPricing) {
    return <Tooltip content={tooltip}>{badge}</Tooltip>;
  }

  return (
    <Tooltip content={`${tooltip} — open the sourcing page`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          openInNewTab(`vehicles/${vehicle.id}`);
        }}
      >
        {badge}
      </button>
    </Tooltip>
  );
}
